import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { api } from '../api.js';
import { useSession } from '../auth-client.js';
import CoverImageUploader from '../components/CoverImageUploader.js';

/**
 * `/groups/new` (PLAN.md Phase 10.18/10.19) — creating a circle is now just
 * a name, the organizer's own display name inside it, and an optional cover
 * image. It used to also demand a sport/competition up front in the same
 * step, which is what `CreateGroupScreen`/`CreateCustomCompetitionScreen`
 * still do — but conflating "start a friend circle" with "configure our
 * first competition" forced a decision (and a full sport-picker screen)
 * before there was even anyone to play with yet.
 *
 * Lands on the circle's own page afterward (competitions, then sharing, then
 * the roster — `GroupScreen`), not the (still-empty) member list on its own:
 * the very first thing an organizer needs to see is a place to add a
 * competition, with sharing one scroll away.
 */
export default function CreateCircleScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const [name, setName] = useState('');
  const [joinAs, setJoinAs] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Defaults the in-circle display name to the organizer's own account name —
  // `!joinAs` keeps it from clobbering anything already typed once the
  // session finishes loading.
  useEffect(() => {
    if (session?.user?.name && !joinAs) setJoinAs(session.user.name);
  }, [session?.user?.name, joinAs]);

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({
        name: name.trim(),
        joinAs: joinAs.trim(),
        ...(imageUrl ? { imageUrl } : {}),
      }),
    onSuccess: async (group) => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      void navigate(`/groups/${group.id}`);
    },
  });

  const canSubmit = name.trim().length > 0 && joinAs.trim().length > 0 && !create.isPending;

  return (
    <main className="screen">
      <Link to="/" className="muted">
        {t('common.back')}
      </Link>
      <h1>{t('createCircle.title')}</h1>
      <p className="muted">{t('createCircle.intro')}</p>

      <form
        className="panel stack"
        style={{ padding: 'var(--space-4)' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="name">
            {t('createCircle.nameLabel')}
          </label>
          <input
            id="name"
            className="input"
            value={name}
            maxLength={80}
            placeholder={t('createCircle.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="joinAs">
            {t('createCircle.joinAsLabel')}
          </label>
          <input
            id="joinAs"
            className="input"
            value={joinAs}
            maxLength={60}
            placeholder={t('createCircle.joinAsPlaceholder')}
            onChange={(event) => setJoinAs(event.target.value)}
          />
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
            {t('createCircle.joinAsHint')}
          </p>
        </div>

        <div>
          <span className="label">{t('createCircle.imageLabel')}</span>
          <CoverImageUploader
            imageUrl={imageUrl}
            onChange={setImageUrl}
            disabled={create.isPending}
          />
        </div>

        {create.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

        <button type="submit" className="button button-primary" disabled={!canSubmit}>
          {create.isPending ? t('createCircle.creating') : t('createCircle.submit')}
        </button>
      </form>
    </main>
  );
}
